/**
 * Performance-pad GrainPlayer with a zone-effect chain.
 *
 * Backing module for the 16-column ScrubPad. The pad's four zones
 * each drive a different effect; this file owns the DSP graph
 * that makes them sound distinct. Signal flow:
 *
 *     GrainPlayer → Gain (gate envelope) → FeedbackDelay → Destination
 *
 * The Gain node is modulated by a square-wave LFO for the Gate
 * zone; for every other zone it stays at unity. FeedbackDelay's
 * `wet` is 0 unless the Delay zone is active. Roll is implemented
 * through the GrainPlayer's own grain-emission rate (grainSize =
 * 1 / desired retrigger rate). Bounce uses a wall-clock timer that
 * re-triggers the player at a new slice position on every tick.
 *
 * We intentionally re-use a single DSP graph across all zones:
 *   - Swapping zones mid-performance is lower-glitch than tearing
 *     a graph down and rebuilding one each time.
 *   - All zones share the same GrainPlayer instance so the working
 *     buffer is loaded exactly once per sample.
 *   - Each zone only needs to set a few params (LFO freq, delay
 *     time, grainSize, bounce interval) — no node swapping.
 *
 * Bundle: imports from the same `tone` namespace that granular.ts
 * already pulls in, so the Slicer's bundle budget doesn't grow.
 */

import { FeedbackDelay, Gain, GrainPlayer, LFO, ToneAudioBuffer } from 'tone'

/**
 * Opt-in verbose diagnostic tracing. Flip on at the console with
 * `window.__scrubDebug = true` to watch every partial that hits the
 * RAF-coalesced queue + every flush. Off by default; the check is a
 * single property read so leaving the calls in costs nothing in the
 * hot path.
 */
function scrubDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false
  return (window as unknown as { __scrubDebug?: boolean }).__scrubDebug === true
}

/** Which zone's effect is currently active. `null` = raw grain
 *  emission with no effect DSP (used as a fallback / initial state). */
export type Zone = 'gate' | 'roll' | 'delay' | 'bounce' | null

export interface ScrubParams {
  /** Position in the source buffer, seconds. */
  positionSec: number
  /** Pitch offset in semitones, -24..+24. */
  pitchSemitones: number
  zone: Zone
  /** 0..3 — which of the 4 variants inside the zone. */
  variant: number
  /** Project tempo. Drives gate rate, roll rate, delay time,
   *  bounce interval. */
  bpm: number
  /** Slice-boundary times (seconds, sorted). Only used by Bounce
   *  zone; other zones ignore. */
  slicePositions?: number[]
  /**
   * How much of the sample the "held-at-this-cell" loop region
   * covers, in beats. Default 1 beat (a 1/4 note). Raise this to
   * audition a longer window starting at the touched position —
   * e.g. 4 = hold a full bar from here.
   *
   * The loop region is always `[positionSec, positionSec + length]`,
   * clamped to the buffer end. If that pushes past the buffer, the
   * loop simply ends at the buffer boundary.
   */
  loopLengthBeats?: number
  /**
   * Explicit grain size in beats, overriding whatever the zone
   * would pick. `null` or undefined → zone default. Exposed so
   * the user can e.g. gate at 1/4 rate while still emitting tight
   * 1/32 grains underneath. Range 1/32 (0.125) through 1/4 (1).
   */
  grainSizeBeats?: number | null
  /**
   * Transposition added on top of `pitchSemitones`. Lets the user
   * lock in a key offset (say +7 st for a fifth up) and still use
   * Y on the grid to modulate ±12 around that centre. Range
   * ±24 st but clamped with `pitchSemitones` into the detune
   * cap (±2400 cents) at write time.
   */
  transposeSemitones?: number
  /** Play the source in reverse. GrainPlayer-native — no buffer
   *  re-render. Useful for Tempera-style reverse swells. */
  reverse?: boolean
}

/**
 * Gate zone — subdivisions per beat, index = variant 0..3.
 * [1/32 (8 per beat), 1/16 (4), 1/8 (2), 1/4 (1)]
 */
const GATE_PER_BEAT = [8, 4, 2, 1]

/**
 * Roll zone — grain retrigger rate (grains per beat).
 * [×16, ×8, ×4, ×2]. Higher variant = tighter.
 */
const ROLL_PER_BEAT = [16, 8, 4, 2]

/**
 * Delay zone variants: [beat fraction, feedback].
 * Fraction is in beats (0.25 = 1/16 note, 0.5 = 1/8).
 */
const DELAY_TIME_BEATS = [0.25, 0.25, 0.5, 0.5]
const DELAY_FEEDBACK = [0.3, 0.7, 0.3, 0.7]

/**
 * Bounce zone — ±range in slice indices per jump.
 * The last variant is treated as "full random" (range ≥ slice count).
 */
const BOUNCE_RANGE = [1, 2, 4, 999]

/**
 * Bounce zone — jumps every 1/8 note. Matches the "8th-note bounce
 * across the sample" the user described. Bounced pitch ± semitones
 * scales with variant for an increasingly chaotic feel.
 */
const BOUNCE_JUMPS_PER_BEAT = 2 // 1/8 note
const BOUNCE_PITCH_RANGE_ST = [0, 2, 7, 12]

export interface ScrubPerformer {
  /** Start / re-trigger playback. Applies all zone DSP settings
   *  and starts grain emission at `positionSec`. */
  play: (params: ScrubParams) => void
  /** Update parameters on a running performer. Position changes
   *  re-seek and may retrigger; zone / variant / bpm changes
   *  reconfigure the effect chain in place. */
  update: (params: Partial<ScrubParams>) => void
  /** Stop grain emission + any in-flight bounce timer. Does not
   *  dispose — `play()` can be called again. The FeedbackDelay
   *  tail from the last Delay zone keeps draining so users hear
   *  the tail after releasing. Call `killTail()` to force silence
   *  immediately. */
  stop: () => void
  /** Immediately zero the delay tail. Companion to `stop()` for
   *  cases where silence matters more than musical decay
   *  (transport stop, sample swap). */
  killTail: () => void
  /** Tear down all Tone nodes. */
  dispose: () => void
}

export async function createScrubPerformer(
  buffer: AudioBuffer,
): Promise<ScrubPerformer> {
  const toneBuffer = new ToneAudioBuffer()
  toneBuffer.set(buffer)
  const player = new GrainPlayer(toneBuffer)
  // Gate envelope. Base value 1 so the chain passes through at
  // unity whenever the LFO isn't actively gating.
  const gain = new Gain(1)
  const lfo = new LFO({
    type: 'square',
    min: 0,
    max: 1,
    frequency: 0,
  })
  lfo.connect(gain.gain)
  // LFO stays dormant until Gate zone engages — `lfo.start()` /
  // `lfo.stop()` is cheaper than leaving a 0Hz oscillator running
  // constantly and keeps the audio graph from doing background
  // DSP work when the user isn't on Gate.
  let lfoRunning = false

  // Parallel delay-send architecture so tails survive zone exit.
  // Series `player → delay → destination` would silence the tail
  // the moment we set `delay.wet = 0` on leaving the Delay zone.
  // Instead we split into dry + send:
  //
  //     player → gain ┬─→ destination              (dry, always)
  //                   └─→ delaySend → delay → destination
  //
  // `delaySend` controls what enters the delay line. Zero it out
  // on zone exit and no new audio feeds the line, but existing
  // content keeps recirculating at the current feedback value and
  // stays audible through `delay.wet = 1`.
  const delay = new FeedbackDelay({
    delayTime: 0.125,
    feedback: 0,
    wet: 1,
  })
  const delaySend = new Gain(0)
  player.connect(gain)
  gain.toDestination()
  gain.connect(delaySend)
  delaySend.connect(delay)
  delay.toDestination()

  let playing = false
  /** Wall-clock timer id for the Bounce zone's position-jump
   *  scheduler. Cleared on zone change / stop / dispose. */
  let bounceTimerId: ReturnType<typeof setInterval> | null = null
  let current: ScrubParams | null = null

  /**
   * RAF-coalesced `update()` buffer. Slider drags + pointer-move
   * handlers can fire dozens of `update()` calls per second, each
   * mutating GrainPlayer params mid-grain — which the internal
   * grain scheduler reads on the NEXT grain emission. If the
   * values change between emissions the result is grains with
   * mismatched params (size / overlap / detune), which sounds like
   * the "starts slipping / grains getting corrupted and scratchy"
   * bug reported against long scrub sessions.
   *
   * Instead, we accumulate all partials into `pendingPartial` and
   * flush them in ONE pass on the next `requestAnimationFrame`. That
   * still feels instant to the user (≤16 ms), but the DSP only sees
   * one coherent param change per frame instead of mid-grain churn.
   *
   * If `window.__scrubDebug = true`, logs every accumulation + flush
   * so the diagnostic path stays cheap to leave in after this fix.
   */
  let pendingPartial: Partial<ScrubParams> | null = null
  let pendingRafId: number | null = null

  const startLfo = (frequency: number) => {
    lfo.frequency.value = frequency
    if (!lfoRunning) {
      lfo.start()
      lfoRunning = true
    }
  }

  const stopLfo = () => {
    if (!lfoRunning) return
    lfo.stop()
    lfoRunning = false
    // LFO stopped → the param it drives holds its last signal
    // value, which for a square wave is either 0 or 1. Pin gain
    // back to unity so the chain passes through cleanly; without
    // this, a zone change that caught the LFO mid-low-phase would
    // leave the slicer silent until the next Gate entry.
    gain.gain.cancelScheduledValues(0)
    gain.gain.value = 1
  }

  const clearBounce = () => {
    if (bounceTimerId !== null) {
      clearInterval(bounceTimerId)
      bounceTimerId = null
    }
  }

  const pickSliceIndex = (
    slices: number[],
    startIdx: number,
    range: number,
  ): number => {
    if (range >= slices.length) {
      return Math.floor(Math.random() * slices.length)
    }
    const offset = Math.floor(Math.random() * (range * 2 + 1)) - range
    // Modular wrap so bouncing off the ends stays musical (jumps
    // to the opposite side rather than clipping at the boundary).
    return (startIdx + offset + slices.length) % slices.length
  }

  const nearestSliceIdx = (slices: number[], target: number): number => {
    let best = 0
    let bestDelta = Math.abs(slices[0] - target)
    for (let i = 1; i < slices.length; i++) {
      const d = Math.abs(slices[i] - target)
      if (d < bestDelta) {
        bestDelta = d
        best = i
      }
    }
    return best
  }

  /**
   * Apply the effect-chain settings for the current zone. Called
   * on every play() and on zone / variant / bpm changes inside
   * update(). Pure mutation of the running DSP graph — no node
   * construction / disposal happens here.
   */
  const applyZone = (params: ScrubParams) => {
    const { zone, variant, bpm } = params
    const bps = bpm / 60 // beats per second
    const safeVariant = Math.max(0, Math.min(3, Math.floor(variant)))

    // --- Gate ---
    if (zone === 'gate') {
      const per = GATE_PER_BEAT[safeVariant]
      startLfo(Math.max(0.1, bps * per))
    } else {
      stopLfo()
    }

    // --- Grain size ---
    // User override wins; otherwise the zone picks. Roll uses
    // grainSize as its retrigger clock so its zone default still
    // matters when override is null.
    const userGrainBeats = params.grainSizeBeats ?? null
    if (userGrainBeats !== null && userGrainBeats > 0) {
      // Slight shrink (0.95×) at user-set grain sizes too, for
      // the same reason Roll applies it — adjacent grains that
      // exactly touch cause clicks on fast rates.
      player.grainSize = Math.max(0.005, (userGrainBeats / bps) * 0.95)
      player.overlap = 0
    } else if (zone === 'roll') {
      const per = ROLL_PER_BEAT[safeVariant]
      const grain = Math.max(0.005, (1 / (bps * per)) * 0.95)
      player.grainSize = grain
      player.overlap = 0
    } else {
      // Default grain: 1/16 at current tempo. Baseline for Gate /
      // Delay / Bounce zones when the user hasn't overridden.
      player.grainSize = Math.max(0.01, 1 / (bps * 4))
      player.overlap = 0
    }

    // --- Reverse ---
    // Cheap to flip at any time; the GrainPlayer picks it up on
    // the next grain. No re-trigger needed.
    player.reverse = params.reverse === true

    // --- Delay ---
    if (zone === 'delay') {
      const frac = DELAY_TIME_BEATS[safeVariant]
      const fb = DELAY_FEEDBACK[safeVariant]
      delay.delayTime.value = Math.max(0.001, frac / bps)
      delay.feedback.value = fb
      // Open the send so new audio enters the delay line.
      delaySend.gain.cancelScheduledValues(0)
      delaySend.gain.setTargetAtTime(1, 0, 0.02)
    } else {
      // Close the send but DON'T zero the delay's feedback — the
      // user expects the tail to keep ringing after they leave
      // the Delay zone. The existing buffer content drains at
      // the last variant's feedback, audible through
      // `delay.wet = 1`. `stop()` + `killTail()` are the two
      // escape hatches for explicit silence.
      delaySend.gain.cancelScheduledValues(0)
      delaySend.gain.setTargetAtTime(0, 0, 0.02)
    }

    // --- Bounce ---
    clearBounce()
    if (
      zone === 'bounce' &&
      params.slicePositions &&
      params.slicePositions.length > 1
    ) {
      const slices = params.slicePositions
      const range = BOUNCE_RANGE[safeVariant]
      const pitchRange = BOUNCE_PITCH_RANGE_ST[safeVariant]
      const intervalMs = 1000 / (bps * BOUNCE_JUMPS_PER_BEAT)
      let anchorIdx = nearestSliceIdx(slices, params.positionSec)
      bounceTimerId = setInterval(() => {
        // Re-read the current params on each tick in case bpm /
        // variant changed since the timer started.
        const p = current
        if (!p || p.zone !== 'bounce') {
          clearBounce()
          return
        }
        const newIdx = pickSliceIndex(slices, anchorIdx, range)
        const newPos = Math.max(
          0,
          Math.min(buffer.duration - 0.01, slices[newIdx]),
        )
        // Final pitch = Y pitch + transpose + (bounce jitter).
        // Transpose still applies in Bounce so the user's key
        // offset follows them across jumps — only the Y value is
        // overridden by the random jitter.
        const base = (p.pitchSemitones ?? 0) + (p.transposeSemitones ?? 0)
        const jitter =
          pitchRange > 0 ? (Math.random() - 0.5) * 2 * pitchRange : 0
        player.detune = Math.max(-2400, Math.min(2400, (base + jitter) * 100))
        // Move the loop region to the new slice WITHOUT a stop +
        // start. GrainPlayer is already running with loop=true so
        // the next emitted grain lands at the new loopStart
        // naturally. Hard-retriggering every 1/8 note (4×/sec at
        // 120 BPM) was the other grain-queue accumulator — each
        // restart queued envelopes that outlived the next grain,
        // which built up into the "gets grainy and never
        // recovers" texture the user reported.
        const loopLenBeats = p.loopLengthBeats ?? 1
        player.loopStart = newPos
        player.loopEnd = Math.min(buffer.duration, newPos + loopLenBeats / bps)
        // Walk the anchor too so successive bounces drift across
        // the sample instead of orbiting one slice forever.
        anchorIdx = newIdx
      }, intervalMs)
    }
  }

  /** Synchronous apply used by the RAF-coalesced `update()` below.
   *  Splitting the body into a helper lets the queued path reuse
   *  the exact logic the pre-fix `update()` ran inline. */
  const applyUpdate = (partial: Partial<ScrubParams>) => {
    if (!current) return
    const next: ScrubParams = { ...current, ...partial }
    current = next

    if (partial.pitchSemitones !== undefined) {
      applyPitchIfStatic(next)
    }

    if (
      partial.zone !== undefined ||
      partial.variant !== undefined ||
      partial.bpm !== undefined ||
      partial.slicePositions !== undefined ||
      partial.grainSizeBeats !== undefined ||
      partial.reverse !== undefined
    ) {
      applyZone(next)
    }

    if (
      playing &&
      next.zone !== 'bounce' &&
      (partial.positionSec !== undefined ||
        partial.loopLengthBeats !== undefined ||
        partial.bpm !== undefined)
    ) {
      const offset = Math.max(
        0,
        Math.min(buffer.duration - 0.005, next.positionSec),
      )
      const bps = next.bpm / 60
      const loopLenBeats = next.loopLengthBeats ?? 1
      player.loopStart = offset
      player.loopEnd = Math.min(buffer.duration, offset + loopLenBeats / bps)
    }

    // Hard retrigger only when the position itself changes (cell-
    // to-cell moves). Loop-length / grain-size / reverse edits take
    // effect on the currently-running grains without a restart —
    // crucial for click-free live tweaking.
    if (
      partial.positionSec !== undefined &&
      playing &&
      next.zone !== 'bounce'
    ) {
      const offset = Math.max(
        0,
        Math.min(buffer.duration - 0.005, partial.positionSec),
      )
      try {
        player.stop()
      } catch {
        /* fine */
      }
      player.start(undefined, offset)
    }
  }

  const applyPitchIfStatic = (params: ScrubParams) => {
    // Final pitch = Y pitch + transpose. Bounce zone handles its
    // own per-jump detune inside the interval callback, but even
    // there `transpose` is layered in — this branch only skips so
    // we don't stomp on the jitter between ticks.
    if (params.zone !== 'bounce') {
      const total =
        (params.pitchSemitones ?? 0) + (params.transposeSemitones ?? 0)
      player.detune = Math.max(-2400, Math.min(2400, total * 100))
    }
  }

  return {
    play(params) {
      current = params
      applyPitchIfStatic(params)
      applyZone(params)

      if (playing) {
        try {
          player.stop()
        } catch {
          /* already stopped is fine */
        }
      }

      const offset = Math.max(
        0,
        Math.min(buffer.duration - 0.005, params.positionSec),
      )
      const bps = params.bpm / 60
      const loopLenBeats = params.loopLengthBeats ?? 1
      const regionEnd = Math.min(buffer.duration, offset + loopLenBeats / bps)
      player.loop = true
      player.loopStart = offset
      player.loopEnd = regionEnd
      // Bounce also starts with one grain — its interval callback
      // replaces the position on the next tick.
      player.start(undefined, offset)
      playing = true
    },
    update(partial) {
      if (!current) return
      // Accumulate into the pending partial so multiple updates in
      // the same frame merge before the DSP graph is touched. Later
      // keys in the same frame win (last-write), same as if the
      // call had applied synchronously — no semantic change from
      // the caller's POV, just a 16ms batching window.
      pendingPartial = { ...(pendingPartial ?? {}), ...partial }
      if (scrubDebugEnabled()) {
        console.debug(
          '[scrub] queued partial:',
          partial,
          '· merged pending:',
          pendingPartial,
        )
      }
      if (pendingRafId !== null) return
      pendingRafId = requestAnimationFrame(() => {
        pendingRafId = null
        if (!current) {
          pendingPartial = null
          return
        }
        const merged = pendingPartial ?? {}
        pendingPartial = null
        if (scrubDebugEnabled()) {
          console.debug('[scrub] flushing merged partial:', merged)
        }
        applyUpdate(merged)
      })
    },
    stop() {
      clearBounce()
      // Drop any queued param updates — they were aimed at a graph
      // that's about to be silent, and flushing them after `stop`
      // would restart the player.
      if (pendingRafId !== null) {
        cancelAnimationFrame(pendingRafId)
        pendingRafId = null
      }
      pendingPartial = null
      if (playing) {
        try {
          player.stop()
        } catch {
          /* fine */
        }
        playing = false
      }
      // Reset the non-delay chain state so the NEXT play() starts
      // clean (detune / reverse / LFO). DO NOT zero delay.wet or
      // delay.feedback here — the user expects the delay tail to
      // keep sounding after they release the Delay zone; killing
      // the wet path cuts the reverb-like ring that's the whole
      // point of the zone. The running `feedback` value will drain
      // the delay buffer on its own over the next few delay-time
      // periods; new audio stops entering the delay because the
      // GrainPlayer isn't feeding it anymore.
      player.detune = 0
      player.reverse = false
      stopLfo()
      current = null
    },
    /** Immediately kill the delay tail. Separate from `stop()` so
     *  releasing a touch leaves the tail audible but the caller
     *  can force silence (transport-stop, sample swap, etc.).
     *  Feedback → 0 drains the line within one delay period; send
     *  stays at 0 so no new audio refills it. */
    killTail() {
      delaySend.gain.cancelScheduledValues(0)
      delaySend.gain.value = 0
      delay.feedback.cancelScheduledValues(0)
      delay.feedback.value = 0
    },
    dispose() {
      clearBounce()
      if (pendingRafId !== null) {
        cancelAnimationFrame(pendingRafId)
        pendingRafId = null
      }
      pendingPartial = null
      try {
        player.stop()
      } catch {
        /* fine */
      }
      player.dispose()
      gain.dispose()
      lfo.dispose()
      delaySend.dispose()
      delay.dispose()
      toneBuffer.dispose()
      playing = false
    },
  }
}
